import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import Button from '../common/Button';
import { LoadingState } from '../common/Spinner';

const MODULE_CONFIG = [
  { key: 'daily_business', label: 'Daily Business', icon: 'fa-clipboard-check', iconBg: 'from-primary to-primary-dark', border: 'hover:border-primary/50 dark:hover:border-primary/60', accent: 'text-primary dark:text-accent focus:ring-primary hover:text-primary', accentBorder: 'hover:border-primary/30' },
  { key: 'stock_check', label: 'Stock Check', icon: 'fa-boxes', iconBg: 'from-green-500 to-emerald-600', border: 'hover:border-green-500/50', accent: 'text-green-600 dark:text-emerald-400 focus:ring-green-600 hover:text-green-600', accentBorder: 'hover:border-green-500/30' },
  { key: 'liter_statement', label: 'Liter Statement', icon: 'fa-file-alt', iconBg: 'from-yellow-500 to-orange-600', border: 'hover:border-yellow-500/50', accent: 'text-yellow-600 dark:text-amber-400 focus:ring-yellow-600 hover:text-yellow-600', accentBorder: 'hover:border-yellow-500/30' }
];

// Permission name -> display label
const permissionDisplayNames = {
  create: 'Create',
  view: 'Read',
  edit: 'Update',
  delete: 'Delete',
  import: 'Import Brand'
};

// Permission order for consistent display
const permissionOrder = ['create', 'view', 'edit', 'delete', 'import'];

const emptyPermissionState = () => ({ daily_business: [], stock_check: [], liter_statement: [] });

const PermissionModal = ({ role, onClose, onUpdate }) => {
  const [permissions, setPermissions] = useState(emptyPermissionState);
  const [availablePermissions, setAvailablePermissions] = useState(emptyPermissionState);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (role) {
      fetchPermissions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const fetchPermissions = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get(`/roles/${role.id}/permissions`);
      const { permissions: rolePermissions } = response.data;

      const formattedAvailablePermissions = emptyPermissionState();
      const formattedPermissions = emptyPermissionState();

      Object.keys(rolePermissions).forEach(module => {
        // Store all available permissions for this module
        formattedAvailablePermissions[module] = rolePermissions[module].map(p => p.name);
        // Store only checked permissions
        formattedPermissions[module] = rolePermissions[module].filter(p => p.checked).map(p => p.name);
      });

      setAvailablePermissions(formattedAvailablePermissions);
      setPermissions(formattedPermissions);
    } catch (error) {
      console.error('Error fetching permissions:', error);
      let errorMessage = 'Failed to load permissions';
      if (error.response) {
        if (error.response.status === 401) {
          errorMessage = 'Your session has expired. Please log in again.';
        } else if (error.response.status === 403) {
          errorMessage = 'You do not have permission to view role permissions.';
        } else if (error.response.data?.error) {
          errorMessage = error.response.data.error;
        }
      } else if (error.message) {
        errorMessage = `Network error: ${error.message}`;
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionChange = (module, permissionName, checked) => {
    setPermissions(prev => {
      const updated = { ...prev };
      if (checked) {
        if (!updated[module].includes(permissionName)) {
          updated[module] = [...updated[module], permissionName];
        }
      } else {
        updated[module] = updated[module].filter(p => p !== permissionName);
      }
      return updated;
    });
  };

  const handleModuleSelectAll = (module, allPermissions, checked) => {
    setPermissions(prev => ({
      ...prev,
      [module]: checked ? [...allPermissions] : []
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      if (!localStorage.getItem('token')) {
        setError('You are not authenticated. Please log in again.');
        setSaving(false);
        return;
      }

      await api.put(`/roles/${role.id}/permissions`, { permissions });

      // Wait for the update callback to complete before closing
      if (onUpdate) {
        await onUpdate();
      }

      onClose();
    } catch (error) {
      console.error('[PERMISSION MODAL] Error saving permissions:', error);

      let errorMessage = 'Failed to save permissions';
      if (error.response) {
        if (error.response.status === 401) {
          errorMessage = 'Your session has expired. Please log in again.';
        } else if (error.response.status === 403) {
          errorMessage = 'You do not have permission to modify role permissions.';
        } else if (error.response.status === 404) {
          errorMessage = 'Role not found.';
        } else if (error.response.data?.error) {
          errorMessage = error.response.data.error;
        } else if (error.response.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data?.errors) {
          errorMessage = Array.isArray(error.response.data.errors)
            ? error.response.data.errors.map(e => e.msg || e.message || JSON.stringify(e)).join(', ')
            : JSON.stringify(error.response.data.errors);
        }
      } else if (error.message) {
        errorMessage = `Network error: ${error.message}`;
      }

      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  // Get all available permissions for a module, in a consistent order
  const getAllPermissions = (module) => {
    if (availablePermissions[module] && availablePermissions[module].length > 0) {
      return permissionOrder.filter(p => availablePermissions[module].includes(p));
    }
    return permissionOrder;
  };

  if (!role) return null;

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 pt-[max(1rem,env(safe-area-inset-top))] pb-[max(1rem,env(safe-area-inset-bottom))] overflow-y-auto"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="permission-modal-title"
    >
      <div
        className="bg-white dark:bg-slate-800 rounded-2xl sm:rounded-3xl p-6 sm:p-8 max-w-4xl w-full shadow-2xl border border-slate-100 dark:border-slate-700 slide-in my-8 max-h-dvh-90 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4 sm:mb-6">
          <h2 id="permission-modal-title" className="text-xl sm:text-2xl md:text-3xl font-bold gradient-text flex items-center gap-2 sm:gap-3">
            <span className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-primary to-accent rounded-xl flex items-center justify-center shrink-0">
              <i className="fas fa-user-shield text-white text-sm sm:text-base" aria-hidden="true"></i>
            </span>
            <span className="break-words">Edit Permissions - {role.name}</span>
          </h2>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-white transition-all duration-300 flex items-center justify-center btn-hover-scale shrink-0"
            aria-label="Close"
          >
            <i className="fas fa-times" aria-hidden="true"></i>
          </button>
        </div>

        {loading ? (
          <LoadingState label="Loading permissions..." />
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="space-y-6">
              {MODULE_CONFIG.map((module) => {
                const allPerms = getAllPermissions(module.key);
                const selected = permissions[module.key];
                return (
                  <div key={module.key} className={`border-2 border-slate-200 dark:border-slate-700 rounded-xl sm:rounded-2xl p-4 sm:p-6 transition-all duration-300 ${module.border}`}>
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-3 sm:mb-4 gap-2 sm:gap-0">
                      <h3 className="text-lg sm:text-xl font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2 sm:gap-3">
                        <span className={`w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br ${module.iconBg} rounded-xl flex items-center justify-center shrink-0`}>
                          <i className={`fas ${module.icon} text-white text-sm sm:text-base`} aria-hidden="true"></i>
                        </span>
                        {module.label}
                      </h3>
                      <label className={`flex items-center gap-2 cursor-pointer text-sm font-semibold text-slate-600 dark:text-slate-300 transition-colors ${module.accent}`}>
                        <input
                          type="checkbox"
                          checked={allPerms.length > 0 && selected.length === allPerms.length}
                          onChange={(e) => handleModuleSelectAll(module.key, allPerms, e.target.checked)}
                          className={`w-4 h-4 rounded focus:ring-2 ${module.accent}`}
                        />
                        Select All
                      </label>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
                      {allPerms.map((permName) => (
                        <label
                          key={permName}
                          className={`flex items-center gap-2 p-2 sm:p-3 bg-slate-50 dark:bg-slate-900/50 rounded-lg sm:rounded-xl hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer transition-all duration-200 border-2 border-transparent ${module.accentBorder}`}
                        >
                          <input
                            type="checkbox"
                            checked={selected.includes(permName)}
                            onChange={(e) => handlePermissionChange(module.key, permName, e.target.checked)}
                            className={`w-4 h-4 sm:w-5 sm:h-5 rounded focus:ring-2 ${module.accent}`}
                            aria-label={`${permissionDisplayNames[permName]} permission for ${module.label}`}
                          />
                          <span className="font-semibold text-slate-700 dark:text-slate-200 text-sm sm:text-base">
                            {permissionDisplayNames[permName]}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {error && (
              <div className="mt-6 p-4 bg-red-50 dark:bg-red-500/10 border-2 border-red-200 dark:border-red-500/30 text-red-600 dark:text-red-400 font-medium text-sm rounded-xl" role="alert">
                <i className="fas fa-exclamation-circle mr-2" aria-hidden="true"></i>
                {error}
              </div>
            )}

            <div className="flex justify-end gap-4 mt-8">
              <Button type="button" variant="secondary" onClick={onClose} disabled={saving}>
                Cancel
              </Button>
              <Button type="submit" loading={saving} icon="fa-save">
                {saving ? 'Saving...' : 'Save Permissions'}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default PermissionModal;
